<script lang="ts">
  /**
   * The shell's one modal surface: a bottom sheet on a phone, a centred
   * dialog from 1024 px up (the `dialog` variant is centred at every width —
   * `ConfirmDialog` is one).
   *
   * Built on the native `<dialog>` and `showModal()`, which does the half that
   * is easy to get wrong by hand: the browser owns the focus trap, the inert
   * page behind and the top layer. What this adds on top:
   *
   * - **Escape closes it, and only it.** The keydown is handled here, with its
   *   default prevented (so the browser does not also run its own close
   *   request) and its propagation stopped — a game screen listening on
   *   `window` for Escape must never see the same press and open something
   *   right behind the sheet that just closed (the lesson `dialogA11y.ts`
   *   records).
   * - **Back closes it.** Opening pushes one history entry through
   *   `historyStack.ts`; a hardware or gesture Back pops it and closes the
   *   sheet, and closing it any other way consumes the same entry — the same
   *   contract every other overlay in the app follows (TwinTen's ADR 0025).
   *   Android Chrome may route Back to the dialog's own close request instead
   *   (`cancel`); that is handled too, and the unconsumed entry is walked back
   *   by the ordinary close.
   * - **Focus lands on `[data-autofocus]`** when the content marks one (a
   *   confirmation's safe action), otherwise on the panel; and it goes back to
   *   whatever had it once the sheet closes.
   * - **The parent owns `open`.** Every way of closing — Escape, Back, the
   *   backdrop, the close button — calls `onclose` with that reason
   *   (`SheetCloseReason`); the sheet never closes itself behind the
   *   parent's back.
   * - **`dismissible={false}`** (since 1.2.0) is for something the game's
   *   state owns and the player cannot wave away: a server-driven lobby, a
   *   pause, a match's end. It draws no close button, and Escape, Back and the
   *   backdrop do not close it: each calls `onbackattempt` with what it was
   *   instead (the game may ask "Leave the room?"). Back still lands on the
   *   sheet's own history entry, which is armed again at once, so a Back
   *   never leaves the app from under it. The game closes it by setting
   *   `open` to false.
   *
   * jsdom has no `showModal()`; without it the element is simply given its
   * `open` attribute, which is enough for everything a unit test asserts.
   */
  import type { Snippet } from 'svelte';

  import { pushOverlay } from '../historyStack.js';
  import { shellText } from '../i18n.svelte.js';
  import type { SheetBackAttempt, SheetCloseReason } from '../types.js';

  interface Props {
    readonly open: boolean;
    /** The sheet's accessible name, and its visible heading unless `header` replaces it. */
    readonly title: string;
    /** Called by Escape, Back, the backdrop and the close button alike, with which it was. */
    readonly onclose: (reason: SheetCloseReason) => void;
    /**
     * `false`: the player cannot dismiss it (no close button; Escape, Back and
     * the backdrop call `onbackattempt` instead of `onclose`). Default `true`.
     */
    readonly dismissible?: boolean;
    /** A non-dismissible sheet's Escape, Back or backdrop press. */
    readonly onbackattempt?: ((reason: SheetBackAttempt) => void) | undefined;
    /** `md` lays the body out as a column with `--ui-space-3` between its children. Default `none`. */
    readonly gap?: 'none' | 'md';
    /** A header of the caller's own (an options sheet's avatar row), drawn instead of the title row. */
    readonly header?: Snippet | undefined;
    /**
     * With `header`: the id of the element in it that shows the title. It
     * names the sheet, and no hidden copy of `title` is drawn (a screen
     * reader would otherwise read the title twice).
     */
    readonly labelledby?: string | undefined;
    readonly children: Snippet;
    /** `sheet` (the default) docks to the bottom on a phone; `dialog` is centred at every width. */
    readonly variant?: 'sheet' | 'dialog';
    /** `alertdialog` for a question that interrupts (a confirmation). */
    readonly role?: 'dialog' | 'alertdialog';
    /** The id of the element that describes the sheet, for `aria-describedby`. */
    readonly describedby?: string | undefined;
    /** The dialog element's test id. */
    readonly testid?: string | undefined;
    /** The close button's test id. Default `ui-sheet-close`. */
    readonly closeTestid?: string | undefined;
  }
  const {
    open,
    title,
    onclose,
    header,
    labelledby,
    children,
    variant = 'sheet',
    role = 'dialog',
    describedby,
    testid,
    closeTestid = 'ui-sheet-close',
    dismissible = true,
    onbackattempt,
    gap = 'none',
  }: Props = $props();

  /** One way out: close when dismissible, otherwise report the attempt. */
  function leave(reason: SheetBackAttempt): void {
    if (dismissible) onclose(reason);
    else onbackattempt?.(reason);
  }

  const titleId = $props.id();

  let dialog = $state<HTMLDialogElement | null>(null);

  function showNative(element: HTMLDialogElement): void {
    if (typeof element.showModal === 'function') {
      try {
        element.showModal();
        return;
      } catch {
        // Not connected yet, or already open non-modally: fall through.
      }
    }
    element.setAttribute('open', '');
  }

  /**
   * How many `close` events our own `close()` calls still owe. The native
   * `close` event is queued, not fired inside `close()` — so on an
   * unmount it arrives after the component is gone, when `open` still
   * reads its last value (`true`), and `onNativeClose` used to answer it
   * with `onclose()`: a `ConfirmDialog` whose action unmounted it then
   * also fired `oncancel`, after `onconfirm` (review finding). Each of our
   * own closes is counted here and its event swallowed.
   */
  let ownCloses = 0;
  let destroyed = false;

  $effect(() => () => {
    destroyed = true;
  });

  function hideNative(element: HTMLDialogElement): void {
    if (typeof element.close === 'function' && element.open) {
      ownCloses += 1;
      element.close();
    } else element.removeAttribute('open');
  }

  // Open and close the native element with the prop, and hand focus back on
  // the way out — including when the parent unmounts the sheet while open.
  $effect(() => {
    const element = dialog;
    if (element === null || !open) return undefined;
    const previouslyFocused = document.activeElement;
    showNative(element);
    const target =
      element.querySelector<HTMLElement>('[data-autofocus]') ??
      element.querySelector<HTMLElement>('[data-sheet-panel]');
    target?.focus({ preventScroll: true });
    return () => {
      hideNative(element);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  });

  // One history entry per open sheet (see the component comment). `onclose`
  // is read when Back actually arrives, never while this effect runs, so a
  // parent passing a fresh arrow function does not re-push anything.
  //
  // A non-dismissible sheet answers Back by arming a fresh entry straight
  // away and reporting the attempt, so the next Back lands here again rather
  // than on whatever is under it. `dismissible` is read when Back arrives,
  // not here, so changing it does not re-push.
  $effect(() => {
    if (!open) return undefined;
    let release: (() => void) | null = null;
    const arm = (): void => {
      release = pushOverlay(() => {
        release = null;
        if (dismissible) {
          onclose('back');
          return;
        }
        arm();
        onbackattempt?.('back');
      });
    };
    arm();
    return () => release?.();
  });

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    leave('escape');
  }

  /** The browser's own close request (Escape it handled, or Android's Back). */
  function onCancel(event: Event): void {
    event.preventDefault();
    leave('back');
  }

  /**
   * The browser closed the element by itself. Our own `close()` (the effect
   * above, on a close *or* an unmount) fires the same event a task later, and
   * then the parent already knows — so those are swallowed by count, and
   * nothing is ever reported from a sheet that is no longer mounted.
   */
  function onNativeClose(): void {
    if (ownCloses > 0) {
      ownCloses -= 1;
      return;
    }
    if (destroyed || dialog === null || !dialog.isConnected) return;
    if (!open) return;
    if (dismissible) {
      onclose('back');
      return;
    }
    // A browser that closed it anyway (a close request it would not let us
    // refuse): it is not the browser's to close. Show it again.
    showNative(dialog);
    onbackattempt?.('back');
  }

  function onBackdropPointerDown(event: Event): void {
    // A press on the <dialog> itself is a press outside the panel: the panel
    // is a child, so anything that reaches here with this target missed it.
    if (event.target === dialog) leave('backdrop');
  }
</script>

<dialog
  bind:this={dialog}
  class="ui-sheet"
  class:ui-sheet--dialog={variant === 'dialog'}
  role={role === 'alertdialog' ? 'alertdialog' : undefined}
  aria-labelledby={header !== undefined && labelledby !== undefined ? labelledby : titleId}
  aria-describedby={describedby}
  aria-modal="true"
  data-testid={testid}
  onkeydown={onKeydown}
  oncancel={onCancel}
  onclose={onNativeClose}
  onpointerdown={onBackdropPointerDown}
>
  <div class="ui-sheet-panel" data-sheet-panel tabindex="-1">
    {#if variant === 'sheet'}
      <div class="ui-sheet-grab" aria-hidden="true"></div>
    {/if}
    {#if header !== undefined}
      <!-- The caller's header names the sheet by its own title (`labelledby`);
           without one, this keeps `aria-labelledby` pointing at a real
           element. -->
      {#if labelledby === undefined}
        <span id={titleId} class="ui-visually-hidden">{title}</span>
      {/if}
      {@render header()}
    {:else}
      <div class="ui-sheet-header">
        <h2 id={titleId} class="ui-sheet-title">{title}</h2>
        {#if variant === 'sheet' && dismissible}
          <button
            type="button"
            class="ui-sheet-close"
            aria-label={shellText('close')}
            data-testid={closeTestid}
            onclick={() => onclose('button')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6 6 18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        {/if}
      </div>
    {/if}
    <div class="ui-sheet-body" class:ui-sheet-body--gap={gap === 'md'}>
      {@render children()}
    </div>
  </div>
</dialog>

<style>
  /*
   * `display` is load-bearing. The user-agent stylesheet hides a closed
   * <dialog> with `display: none`, and any `display` of our own on the bare
   * element overrides that — leaving a closed sheet invisible but still
   * covering the page and eating every tap. `:not([open])` puts it back.
   */
  .ui-sheet {
    position: fixed;
    inset: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    width: 100%;
    max-width: none;
    height: 100dvh;
    max-height: 100dvh;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-sheet:not([open]) {
    display: none;
  }

  .ui-sheet::backdrop {
    background: var(--ui-scrim);
  }

  .ui-sheet-panel {
    width: min(var(--ui-sheet-max), 100%);
    max-height: 88dvh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    background: var(--ui-surface);
    color: var(--ui-ink);
    border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
    padding: 0 var(--ui-space-4) calc(var(--ui-space-5) + env(safe-area-inset-bottom));
    outline: none;
  }

  .ui-sheet-grab {
    flex: 0 0 auto;
    width: 2.5rem;
    height: 0.3rem;
    border-radius: 999px;
    background: var(--ui-muted);
    align-self: center;
    margin: var(--ui-space-2) 0;
  }

  .ui-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-3);
    min-height: 2.75rem;
    padding: var(--ui-space-1) 0 var(--ui-space-2);
  }

  .ui-sheet-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .ui-sheet-close {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: var(--ui-surface-2);
    color: var(--ui-ink);
  }

  .ui-sheet-body {
    display: flex;
    flex-direction: column;
  }

  .ui-sheet-body--gap {
    gap: var(--ui-space-3);
  }

  .ui-sheet-close:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* The `dialog` variant: centred and compact at every width. */
  .ui-sheet--dialog {
    align-items: center;
    padding: var(--ui-space-4);
  }

  .ui-sheet--dialog .ui-sheet-panel {
    width: min(24rem, 100%);
    border-radius: var(--ui-radius-lg);
    padding: var(--ui-space-5) var(--ui-space-5) var(--ui-space-4);
  }

  /*
   * 1024 px: TwinTen's desktop breakpoint, written out because scoped CSS
   * cannot put a custom property in a media query. A sheet docked to the
   * bottom edge of a wide window is a phone gesture on a screen with no thumb
   * — above it, the sheet is a centred modal, rounded all round.
   */
  @media (min-width: 1024px) {
    .ui-sheet {
      align-items: center;
    }

    .ui-sheet-panel {
      width: min(35rem, calc(100% - 3rem));
      max-height: 80vh;
      border-radius: var(--ui-radius-lg);
    }

    .ui-sheet-grab {
      display: none;
    }

    .ui-sheet--dialog .ui-sheet-panel {
      width: min(26rem, calc(100% - 3rem));
    }
  }

  /* Beside the tablet rail (1.6.0): while an `AppFrame` draws its rail, the
     sheet (or the dialog) is centred over what is right of it, not over the
     whole window; the scrim still covers the rail. The rail is 5.5rem, after
     the frame's left safe area. The range is written out: no range syntax
     (Safari before 16.4). The selector matches while a frame with the rail
     is in the document, wherever this sheet sits; it assumes the frame
     starts at the window's left edge (AppFrame warns in development when it
     does not). */
  @media (min-width: 600px) and (max-width: 1023.98px) {
    :global(:root:has(.ui-frame--rail)) .ui-sheet {
      box-sizing: border-box;
      padding-left: calc(5.5rem + env(safe-area-inset-left));
    }

    :global(:root:has(.ui-frame--rail)) .ui-sheet--dialog {
      padding-left: calc(5.5rem + env(safe-area-inset-left) + var(--ui-space-4));
    }

    /* The same beside a frame's first frame (1.8.0, `firstFrame="auto"`). */
    :global(:root:has(.ui-frame--rail-auto)) .ui-sheet {
      box-sizing: border-box;
      padding-left: calc(5.5rem + env(safe-area-inset-left));
    }

    :global(:root:has(.ui-frame--rail-auto)) .ui-sheet--dialog {
      padding-left: calc(5.5rem + env(safe-area-inset-left) + var(--ui-space-4));
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .ui-sheet[open] .ui-sheet-panel {
      animation: ui-sheet-rise 0.18s ease-out;
    }
  }

  @keyframes ui-sheet-rise {
    from {
      transform: translateY(12px);
      opacity: 0.4;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
</style>
